import React from "react";
import {
    Alert, Button, Modal, Steps,
} from "antd";
import {connect} from "react-redux";
import {getTranslate, Translate, TranslateFunction} from "react-localize-redux";
import {history, Nullable} from "@data-force/app";
import StoreState from "../../interfaces/store/storeState";
import "./InitiateWorkDialog.scss";
import {ClaimJobPhase, JobEntity} from "../../interfaces/store/actions/payload/job";
import {
    ClaimJobPhaseAction,
    ClearJobListDataAction,
    SetFilterTypeAction,
    ToggleInitiateWorkDialogAction,
} from "../../interfaces/store/actions/job";
import {
    claimJobPhase, clearJobListData, setFilterType, toggleInitiateWorkDialog,
} from "../../store/actions/job";
import {formatString, getFormattedTime, getHistoryParams} from "../../utils/common";
import TaskDetails from "../TaskDetails/TaskDetails";
import {ContentType, FilterType} from "../../enums/enums";
import HistoryParams from "../../interfaces/historyParams";
import {setActiveTestType} from "../../store/actions/test";
import {SetActiveTestTypeAction} from "../../interfaces/store/test";
import {ENTER_KEY} from "../../utils/constants";
import TaskDirections from "../TaskDirections/TaskDirections";
import {TabManager} from "../../utils/TabManager";
import JobTitle from "../common/JobTitle/JobTitle";

interface State {
    currentStep: number
    disabledStart: boolean
}

interface StateProps {
    visible: boolean
    translate: TranslateFunction
    data: JobEntity
    isMobile: boolean
}

interface DispatchProps {
    toggleInitiateWorkDialog(payload: Nullable<JobEntity>): ToggleInitiateWorkDialogAction

    setActiveTestType(payload: string): SetActiveTestTypeAction

    setFilterType(payload: string): SetFilterTypeAction

    clearJobListData() : ClearJobListDataAction

    claimJobPhase(payload: ClaimJobPhase): ClaimJobPhaseAction
}

type Props = StateProps & DispatchProps;

class InitiateWorkDialog extends React.Component<Props, State> {
    private static readonly translationPath: string = "components.InitiateWorkDialog.InitiateWorkDialog";

    state: State = {
        currentStep: 0,
        disabledStart: true,
    };

    constructor(props: Props) {
        super(props);

        this.resetModal = this.resetModal.bind(this);
        this.onStepsChange = this.onStepsChange.bind(this);
        this.onStepsKeyDown = this.onStepsKeyDown.bind(this);
        this.enableStartButton = this.enableStartButton.bind(this);
        this.startWorkHandler = this.startWorkHandler.bind(this);
    }

    /**
     * On change of current active step we update state,
     * then we use step to update visibility of details/directions components
     * @param {number} currentStep
     */
    onStepsChange(currentStep: number): void {
        this.setState({currentStep});
    }

    /**
     * On steps key down event, on enter key we update current step in state,
     * this keeps track of currently visible directions/description
     * @param {number} currentStep
     * @param {React.KeyboardEvent} event
     */
    onStepsKeyDown(currentStep: number, event: React.KeyboardEvent): void {
        const {key}: React.KeyboardEvent = event as React.KeyboardEvent;
        if (key === ENTER_KEY) {
            this.setState({currentStep});
        }
    }

    /**
     * Returns the initial message and additional warning messages in case it is a mobile device or if the time limit has been set
     * @param {Nullable<number>} timeLimit
     * @return {ReactNode}
     */
    getAlertMsg(timeLimit: Nullable<number>): React.ReactNode {
        const {translate, isMobile}: Props = this.props;
        const {translationPath}: typeof InitiateWorkDialog = InitiateWorkDialog;

        return (
            <>
                <Translate id={`${translationPath}.pleaseNote.text`}/>
                <strong><Translate id={`${translationPath}.initialWarning.text`}/></strong>
                {isMobile && <Translate id={`${translationPath}.mobileWarning.text`}/>}
                {timeLimit && formatString(
                    translate(`${translationPath}.timeWarning.text`) as string, getFormattedTime(timeLimit, translate),
                )}
            </>
        );
    }

    /**
     * Enables start button
     */
    enableStartButton(): void {
        this.setState({disabledStart: false});
    }

    /**
     * Reset modal state, closes the modal.
     */
    resetModal(): void {
        this.setState((prevState: State): State => ({
            ...prevState,
            currentStep: 0,
            disabledStart: true,
        }), this.props.toggleInitiateWorkDialog.bind(this, null));
    }

    /**
     * Initiate starting of work for tests.
     * We redirect to editor, reset list data and set active test tab data.
     */
    startWorkHandler(): void {
        const {
            name: phaseName, projectId, jobId, open = false, title, test,
        }: JobEntity = this.props.data as JobEntity;

        TabManager.getInstance().openTab(null);

        this.props.claimJobPhase({
            projectId,
            jobId,
            phaseName,
            openEditor: true,
            open,
            title,
            test,
        });

        this.resetModal();
        this.props.clearJobListData();
        this.props.setActiveTestType(ContentType.IN_PROGRESS);
        this.props.setFilterType(FilterType.ONGOING);

        const historyParams: Nullable<HistoryParams> = getHistoryParams();
        history.push(`/${historyParams?.idpId}/qualifications`);
    }

    render(): React.ReactNode {
        const {
            visible,
            translate,
            data: {
                name = "", title = "", timeLimit, projectType, longDescriptionRefId,
            },
        }: Props = this.props;
        const {currentStep, disabledStart}: State = this.state;
        const {translationPath}: typeof InitiateWorkDialog = InitiateWorkDialog;

        return (
            <Modal
                className="df-initiate-work-modal"
                title={<JobTitle {...{translate, title, phaseName: name}}/>}
                destroyOnClose
                closable
                visible={visible}
                centered
                width={1300}
                onCancel={this.resetModal}
                footer={[
                    <Button
                        key="cancel"
                        onClick={this.resetModal}
                    >
                        <Translate id={`${translationPath}.cancel.text`}/>
                    </Button>,
                    <Button
                        key="start"
                        className="df-initiate-work-start-btn"
                        type="primary"
                        onClick={this.startWorkHandler}
                        disabled={!!longDescriptionRefId && disabledStart}
                    >
                        <Translate id={`${translationPath}.start.text`}/>
                    </Button>,
                ]}
            >
                <Alert
                    message={this.getAlertMsg(timeLimit)}
                    closable
                    type="info"
                    className="df-initiate-work-info-msg"
                />
                <div className="df-initiate-work-container">
                    <div className="df-initiate-work-steps">
                        <Steps
                            progressDot
                            direction="vertical"
                            onChange={this.onStepsChange}
                            current={currentStep}
                        >
                            <Steps.Step
                                // @ts-ignore (antd docs dont document onKeyDown for this component)
                                onKeyDown={this.onStepsKeyDown.bind(this, 0)}
                                title={<Translate id={`${translationPath}.summaryStepTitle.text`}/>}
                                subTitle={<Translate id={`${translationPath}.summaryStepSubTitle.text`}/>}
                            />
                            {!!longDescriptionRefId && (
                                <Steps.Step
                                    // @ts-ignore
                                    onKeyDown={this.onStepsKeyDown.bind(this, 1)}
                                    title={<Translate id={`${translationPath}.directionsStepTitle.text`}/>}
                                    subTitle={<Translate id={`${translationPath}.directionsStepSubTitle.text`}/>}
                                />
                            )}
                        </Steps>
                    </div>
                    <div className="df-initiate-work-content">
                        {currentStep === 0 && (
                            <TaskDetails key="initiate-work-details"/>
                        )}
                        {(currentStep === 1 && !!longDescriptionRefId) && (
                            <TaskDirections
                                projectType={projectType}
                                readThroughContent
                                contentReadHandler={this.enableStartButton}
                            />
                        )}
                    </div>
                </div>
            </Modal>
        );
    }
}

function mapStateToProps(state: StoreState): StateProps {
    return {
        visible: state.job.initiateWorkDialogVisible,
        translate: getTranslate(state.localize),
        data: state.job.selectedJob || {} as JobEntity,
        isMobile: state.mobile.isMobile,
    };
}

const mapDispatchToProps: DispatchProps = {
    toggleInitiateWorkDialog,
    setActiveTestType,
    setFilterType,
    clearJobListData,
    claimJobPhase,
};

export default connect(mapStateToProps, mapDispatchToProps)(InitiateWorkDialog);
